import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  User, 
  Search, 
  ToggleLeft, 
  ToggleRight,
  Key,
  Heart
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCustomers() {
  const { getAuthHeader } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [passwordDialog, setPasswordDialog] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/customers`, {
        headers: getAuthHeader()
      });
      setCustomers(response.data.customers);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (customerId, currentStatus) => {
    try {
      await axios.put(`${API_URL}/api/admin/customers/${customerId}/status`, 
        { is_active: !currentStatus },
        { headers: getAuthHeader() }
      );
      setCustomers(prev => prev.map(c => 
        c.id === customerId ? { ...c, is_active: !currentStatus } : c
      ));
      toast.success(`Customer ${!currentStatus ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const updatePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      await axios.put(`${API_URL}/api/admin/customers/${passwordDialog}/password`, 
        { new_password: newPassword },
        { headers: getAuthHeader() }
      );
      toast.success('Password updated');
      setPasswordDialog(null);
      setNewPassword('');
    } catch (error) {
      toast.error('Failed to update password');
    }
  };

  const filteredCustomers = customers.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-navy-200 rounded w-1/4" />
          <div className="space-y-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-20 bg-navy-200 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="admin-customers">
      <div className="mb-6">
        <h1 className="text-2xl font-heading font-bold text-navy-900">Manage Customers</h1>
        <p className="text-navy-600">{customers.length} customers registered</p>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-navy-400" />
        <Input
          placeholder="Search by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="search-input"
        />
      </div>

      {/* Customers List */}
      {filteredCustomers.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <User className="w-12 h-12 mx-auto mb-4 text-navy-300" />
            <p className="text-navy-600">No customers found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredCustomers.map((customer) => (
            <Card key={customer.id} data-testid={`customer-card-${customer.id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-6 h-6 text-sky-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-navy-900">{customer.name}</h3>
                        <Badge className={customer.is_active ? 'bg-green-500' : 'bg-red-500'}>
                          {customer.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <p className="text-sm text-navy-600">{customer.email}</p>
                      {customer.phone && (
                        <p className="text-sm text-navy-500">{customer.phone}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-navy-400">
                        <span className="flex items-center gap-1">
                          <Heart className="w-3 h-3" />
                          {customer.favorites?.length || 0} favorites
                        </span>
                        <span>Joined: {new Date(customer.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setPasswordDialog(customer.id)}
                      className="gap-2"
                      data-testid={`password-btn-${customer.id}`}
                    >
                      <Key className="w-4 h-4" />
                      Password
                    </Button>
                    <Button 
                      variant={customer.is_active ? 'destructive' : 'default'}
                      size="sm"
                      onClick={() => toggleStatus(customer.id, customer.is_active)}
                      className="gap-2"
                      data-testid={`toggle-btn-${customer.id}`}
                    >
                      {customer.is_active ? (
                        <>
                          <ToggleRight className="w-4 h-4" />
                          Deactivate
                        </>
                      ) : (
                        <>
                          <ToggleLeft className="w-4 h-4" />
                          Activate
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Password Dialog */}
      <Dialog open={!!passwordDialog} onOpenChange={() => setPasswordDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              type="password"
              placeholder="New password (min 6 characters)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              data-testid="new-password-input"
            />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setPasswordDialog(null)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={updatePassword} className="flex-1 bg-navy-900 hover:bg-navy-800">
                Update Password
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
